import React from 'react';
import PropTypes from 'prop-types';
import ServiceCard from '../ServiceCard';
import OTButton from '../../tag/OTButton';
import OTButtonGroup from '../../tag/OTButtonGroup';
import Span from '../../tag/Span';
import Row from '../../tag/Row';
import Alert from '../../Alert';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
  KAYTTAJA,
  PYYDETTY,
  compareStatus,
} from '../../../definitions/NoonaConstants';
import CheckPhoneNumberForm from './CheckPhoneNumberForm';
import PasswordRenewalModal from './PasswordRenewalModal';
import {
  activateNoonaAccount,
  cancelNoonaAccountActivation,
  doPasswordRenewalRequest,
  initNoonaAccountActivation,
  passwordRenewalModalVisibility,
} from '../../../data/service/noona/actions';
import ToastHOC from '../../ToastHOC';
import { getPhoneNumber } from '../../../data/user/profile/selectors';
import WindowUtils from '../../../utils/WindowUtils';
import { doRequestDismissNotifications } from '../../../data/notification/actions';
import enabledlogoSrc from '../../../images/tays-logo.png';
import disabledLogoSrc from '../../../images/tays-logo-gray.png';

const { id: module_id } = module;

const withNoona = (WrappedComponent) => {
  return class extends React.Component {
    static propTypes = {
      data: PropTypes.object,
      activationInitialized: PropTypes.bool,
      activationIsFetching: PropTypes.bool,
      initAccountActivation: PropTypes.func,
      cancelAccountActivation: PropTypes.func,
      activateAccount: PropTypes.func,
      requestNewPassword: PropTypes.func,
      phoneNumber: PropTypes.string,
      passwordRenewalModalVisible: PropTypes.bool,
      hidePasswordRenewalModal: PropTypes.func,
      showPasswordRenewalModal: PropTypes.func,
    };

    render() {
      const {
        data,
        activationInitialized,
        activationError,
        activationIsFetching,
        initAccountActivation,
        cancelAccountActivation,
        dismissServiceActivationNotification,
        activateAccount,
        requestNewPassword,
        phoneNumber,
        passwordRenewalModalVisible,
        hidePasswordRenewalModal,
        showPasswordRenewalModal,
        toasts,
      } = this.props;

      let message = <FormattedMessage id={`noona.status.${data.status}`} />;
      let content = null;
      let buttons = [];

      if (compareStatus(data.status, KAYTTAJA)) {
        buttons.push(
          <OTButton
            key="go-to-service"
            onClick={() =>
              WindowUtils.openInNewTab('https://europe.patient.noonaclinic.eu/patient/#/sign-in?clinic=TAYS')
            }
            message={'go.to.service'}
          ></OTButton>
        );
        buttons.push(
          <OTButton
            key="request-new-password"
            onClick={showPasswordRenewalModal}
            message="request.new.password"
          />
        );
      } else if (compareStatus(data.status, PYYDETTY)) {
        if (activationInitialized) {
          // Activation has been initialized.
          // Check the phone number.
          content = (
            <Row>
              <Span>
                <CheckPhoneNumberForm />
              </Span>
            </Row>
          );
          if (activationError) {
            buttons.push(
              <OTButton
                key="return"
                onClick={cancelAccountActivation}
                message="return"
              />
            );
          } else {
            buttons.push(
              <OTButton
                disabled={activationIsFetching}
                key="cancel"
                onClick={cancelAccountActivation}
                message="cancel"
              />
            );
            buttons.push(
              <OTButton
                disabled={activationIsFetching}
                key="activate"
                onClick={activateAccount}
                message="activate.account"
              />
            );
          }
        } else {
          // Activation has not been initialized or has been canceled.
          // Show only button to start activation with
          buttons.push(
            <OTButton
              key="begin.activation"
              onClick={() => {
                //dismiss notification in case
                initAccountActivation(phoneNumber);
                dismissServiceActivationNotification('NOONA_PAGE_NOTIFICATION');
              }}
              message="activate.account"
            />
          );
        }
      }

      return (
        <WrappedComponent
          message={message}
          name={'noona.servicecard.title'}
          disabled={compareStatus(data.status, PYYDETTY)}
          enabledlogoSrc={enabledlogoSrc}
          disabledLogoSrc={disabledLogoSrc}
        >
          {content}
          {toasts.map((item) => (
            <Alert {...item.spec} />
          ))}
          <Row>
            <Span len="8">
              <OTButtonGroup
                collapseOnMobile
                expectedCount="2"
                type="big"
                fullWidth={true}
              >
                {buttons}
              </OTButtonGroup>
            </Span>
          </Row>
          {
            <PasswordRenewalModal
              isOpen={passwordRenewalModalVisible}
              hideModal={hidePasswordRenewalModal}
              submit={requestNewPassword}
            />
          }
        </WrappedComponent>
      );
    }
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    initAccountActivation: (originalValue) =>
      dispatch(initNoonaAccountActivation(originalValue)),
    cancelAccountActivation: () => dispatch(cancelNoonaAccountActivation()),
    dismissServiceActivationNotification: (notificationId) =>
      dispatch(doRequestDismissNotifications([notificationId])),
    activateAccount: () => {
      dispatch(activateNoonaAccount());
    },
    showPasswordRenewalModal: () => {
      dispatch(passwordRenewalModalVisibility(true));
    },
    hidePasswordRenewalModal: () => {
      dispatch(passwordRenewalModalVisibility(false));
    },
    requestNewPassword: () => {
      dispatch(doPasswordRenewalRequest());
    },
  };
};

const mapStateToProps = (state) => {
  let { activation, passwordRenewal } = state.services.noona;
  let phoneNumber = getPhoneNumber(state);
  return {
    activationInitialized: activation.initialized,
    activationError: activation.error,
    activationIsFetching: activation.isFetching,
    passwordRenewalModalVisible: passwordRenewal.modalVisible,
    phoneNumber: phoneNumber,
  };
};

const toastOptions = {
  module_id,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ToastHOC(toastOptions, withNoona(ServiceCard)));
